<template lang="pug">
  //- Selected Works (build brief section 6): the Overview slideshow, now cycling in order, with a bottom bar that
  //- scrolls straight down into the linked project, drag / swipe between works, and optional sound.
  article.home(:class="{'home--expanded': expanded}")
    h1.sr-only Guido van Helten

    //- slideshow stage
    figure.home__stage.relative.overflow-hidden.trans-theme.m-0(v-if="doc", ref="stage", :class="[theme === 'dark' && 'text-white', {'home__stage--dragging': dragging}]", :style="stageStyle", @mousemove="onMouse", @mouseleave="onStageLeave", @wheel="onWheel")
      //- media slides (the current slide plus either neighbour hold a Vimeo player and slide
      //- into place following a drag, Instagram-style, instead of the old cross-fade, B3)
      .home__slides.absolute.inset-0
        component.home__slide(
          v-for="(slice, i) in slices",
          :is="slice.slice_type",
          :key="i",
          :slice="slice",
          :index="i",
          :visible="active === i",
          :preload="isAdjacent(i)",
          :near="isNear(i)",
          :muted="muted",
          @ready="onSlideReady(i)",
          @unready="onSlideUnready(i)",
          @error="onSlideError(i)",
          :style="trackStyle(i, active, slices.length)")

      //- right-edge arrow (B3): fades in near the edge, vertically centred, advances to the next work
      button.home__edge-arrow.hidden.lg-flex.items-center.justify-center.absolute.z-20.right-0.p-24(v-if="slices.length > 1", :class="{'home__edge-arrow--visible': showEdgeArrow}", style="top:50%; transform:translateY(-50%)", @click.stop="goTo(1)", aria-label="Next work")
        svg.home__edge-chevron(viewBox="0 0 24 24", aria-hidden="true")
          path(d="M9 5l7 7-7 7", fill="none", stroke="currentColor", stroke-width="1.5")

      //- caption strip: existing caption format, the "More" link is gone (B2)
      figcaption.home__caption.absolute.z-20.left-0.bottom-0.w-full.pb-29.tracking-40.type-body.pointer-events-none(v-if="activeSlice", :class="{'home__caption--hidden': barVisible}")
        .relative.flex.w-full.items-center.justify-between.sm-px-12.trans-inherit
          .absolute.bottom-0.left-0.w-full.px-24.sm-px-12.trans-inherit.sm-static.sm-flex.lg-w-15x18
            h6.inline.sm-block.sm-truncate.mr-2sp
              prismic-rich-text.inline.children-inline.child-italic-font-medium(v-if="hasTxt(activeSlice.primary.custom_caption)", :field="activeSlice.primary.custom_caption")
              span(v-else-if="activePrj") <i class="font-medium">{{ txt(activePrj.data.title) }}</i>, {{ txt(activePrj.data.location) }}, {{ prjYear(activePrj) }}
          //- counter with quiet direction cues (B3)
          .hidden.lg-flex.items-center.px-12.whitespace-no-wrap.font-numbers-normal.pointer-events-auto(v-if="slices.length > 1")
            button.home__sound.mr-2sp.text-gray-500.hover-text-black.trans-color-fast(v-if="canSound", @click.stop="toggleSound", :aria-pressed="String(!muted)") {{ muted ? 'Sound off' : 'Sound on' }}
            button.home__arrow.px-5(@click.stop="goTo(-1)", aria-label="Previous work")
              svg.home__chevron(viewBox="0 0 24 24", aria-hidden="true")
                path(d="M15 5l-7 7 7 7", fill="none", stroke="currentColor", stroke-width="1.5")
            span {{active + 1}}&mdash;{{slices.length}}
            button.home__arrow.px-5(@click.stop="goTo(1)", aria-label="Next work")
              svg.home__chevron(viewBox="0 0 24 24", aria-hidden="true")
                path(d="M9 5l7 7-7 7", fill="none", stroke="currentColor", stroke-width="1.5")

      //- bottom bar (B2): slides up from the bottom edge, click or scroll to go down into the linked project
      //- desktop rises higher to also show the summary/byline (the project intro), so the bar doesn't
      //- read as just a preview-page label; mobile keeps the original compact title-only bar since it
      //- has no hover state to gate a taller reveal and stays permanently on screen (barVisible, B2 mobile)
      .home__bar.absolute.z-30.left-0.bottom-0.w-full.bg-white.text-black.type-body.tracking-40(v-if="activePrj", :class="{'home__bar--visible': barVisible}", @click="onBarClick", role="button", tabindex="0", @keydown.enter="expand", :aria-hidden="String(!barVisible)")
        .px-24.py-18
          .relative
            .flex.items-center.justify-between
              h6.home__bar-title.truncate.mr-2sp
                prismic-rich-text.inline.children-inline.child-italic-font-medium(v-if="hasTxt(activeSlice.primary.custom_caption)", :field="activeSlice.primary.custom_caption")
                span(v-else) <i class="font-medium">{{ txt(activePrj.data.title) }}</i>, {{ txt(activePrj.data.location) }}, {{ prjYear(activePrj) }}
              .flex.items-center.whitespace-no-wrap.text-gray-500
                button.home__sound.mr-2sp.hover-text-black.trans-color-fast(v-if="canSound", @click.stop="toggleSound", :aria-pressed="String(!muted)") {{ muted ? 'Sound off' : 'Sound on' }}
                span.hidden.lg-inline-block.ml-2sp.font-numbers-normal(v-if="slices.length > 1") {{active + 1}}&mdash;{{slices.length}}
            //- scroll cue, centred in the bar: the one thing telling you the project is below
            .home__scroll-cue.hidden.md-flex.absolute.inset-0.items-center.justify-center.pointer-events-none(aria-hidden="true")
              svg.home__scroll-arrow(viewBox="0 0 24 24")
                path(d="M12 4.5v14M5.5 12.5l6.5 6.5 6.5-6.5", fill="none", stroke="currentColor", stroke-width="1.6", stroke-linecap="round", stroke-linejoin="round")
          prismic-rich-text.home__bar-summary.hidden.md-block.type-body.fw-body.tracking-20.leading-wide.mt-12(v-if="hasTxt(activePrj.data.summary)", :field="activePrj.data.summary")
          prismic-rich-text.home__bar-summary.hidden.md-block.type-body.fw-body.tracking-20.leading-wide.text-gray-500(v-if="hasTxt(activePrj.data.byline)", :field="activePrj.data.byline")

    //- the linked project, rendered inline below the slideshow (B2)
    section.home__project.bg-white.text-black(v-if="expanded && expandedUid", ref="project")
      project-page(:uid="expandedUid", :inline="true", @loaded="onProjectLoaded", @onEndTo="$emit('onEndTo', $event)")
</template>

<script>
import store from '@/store'
import { mapState } from 'vuex'
import ImageSlide from '@/components/Home/Home__Slide--Image'
import VimeoSlide from '@/components/Home/Home__Slide--Vimeo'
import ProjectPage from '@/components/Project/ProjectPage'
import { next, prjYear, isTouchDevice } from '@/utils'
import DragTrack from '@/mixins/DragTrack'
import _throttle from 'lodash/throttle'
import _get from 'lodash/get'

const LOAD_TIMEOUT = 12000 // ms: a slide that has not started showing imagery by then is skipped (B1)

export default {
  name: 'home',
  mixins: [DragTrack],
  data () {
    return {
      active: 0,
      ready: {}, // slide index -> is showing imagery
      failed: {}, // slide index -> embed failed or timed out
      autoplayTmOut: null,
      loadTmOut: null,
      // bottom bar and inline project (B2)
      nearBottom: false,
      nearRightEdge: false,
      isTouch: false,
      expanded: false,
      expandedUid: null,
      jumpRequested: false, // true only once the user has actually asked to jump into the project
      pushedHistory: false,
      inProject: false,
      // sound (B4)
      muted: true,
      txt: this.$prismic.richTextAsPlain,
      prjYear
    }
  },
  computed: {
    ...mapState(['theme', 'winW', 'winH']),
    doc () {
      return this.$store.state.singles['home']
    },
    // Prismic order, no shuffle and no re-arranging: a 4-slide deck cycles 1, 2, 3, 4, 1 (B1)
    slices () {
      if (!this.doc) return []
      return this.doc.data.body.filter(slice => slice && slice.primary)
    },
    activeSlice () {
      return this.slices[this.active]
    },
    activePrj () {
      const prj = _get(this.activeSlice, 'primary.link_to')
      return prj && prj.uid && prj.data ? prj : null
    },
    autoplayTimer () {
      return _get(this.doc, 'data.autoplay_timer')
    },
    canSound () {
      const slice = this.activeSlice
      return !!(slice && slice.slice_type === 'vimeo_slide' && slice.primary.allow_sound)
    },
    // desktop: pointer near the bottom edge; touch: always up, since there is no hover
    barVisible () {
      return !!this.activePrj && !this.inProject && (this.isTouch || this.nearBottom)
    },
    // desktop only: pointer near the right edge and not mid-drag (B3)
    showEdgeArrow () {
      return !this.isTouch && !this.inProject && this.slices.length > 1 && this.nearRightEdge && !this.dragging
    },
    stageStyle () {
      return {
        height: `${this.winH}px`,
        // before the project is loaded there is nothing to scroll, so every touch gesture is ours
        touchAction: this.expanded ? 'pan-y' : 'none'
      }
    }
  },
  methods: {
    hasTxt (field) {
      const txt = field && this.$prismic.richTextAsPlain(field)
      return txt && txt.length
    },
    // either neighbour: both can be dragged into view, so both preload a poster/player (B3)
    isAdjacent (i) {
      return this.slices.length > 1 &&
        (i === next(this.active, 1, this.slices.length) || i === next(this.active, -1, this.slices.length))
    },
    // within two steps either way around the loop: the slide's photograph is fetched ahead, so a quick
    // double swipe lands on an image rather than an empty frame while its player loads
    isNear (i) {
      const n = this.slices.length
      const d = (((i - this.active) % n) + n) % n
      return Math.min(d, n - d) <= 2
    },
    goTo (incr = 1) {
      if (this.slices.length < 2) return
      this.active = next(this.active, incr, this.slices.length)
    },

    // --- autoplay (B1, H1): the countdown only starts once the active slide is actually showing imagery
    startCountdown () {
      this.pause()
      if (!this.autoplayTimer || this.inProject || document.hidden) return
      if (!this.ready[this.active]) return // onSlideReady will call again
      this.autoplayTmOut = setTimeout(() => this.goTo(1), this.autoplayTimer * 1000)
    },
    pause () {
      clearTimeout(this.autoplayTmOut)
      this.autoplayTmOut = null
    },
    armLoadTimeout () {
      clearTimeout(this.loadTmOut)
      if (this.ready[this.active]) return
      this.loadTmOut = setTimeout(() => {
        if (this.ready[this.active]) return
        console.warn(`Selected Works: slide ${this.active + 1} did not start in time, skipping`)
        this.$set(this.failed, this.active, true)
        this.skipIfPossible()
      }, LOAD_TIMEOUT)
    },
    skipIfPossible () {
      const total = this.slices.length
      const allFailed = this.slices.every((s, i) => this.failed[i])
      if (total < 2 || allFailed) return
      this.goTo(1)
    },
    onSlideReady (i) {
      this.$set(this.ready, i, true)
      this.$set(this.failed, i, false)
      if (i !== this.active) return
      clearTimeout(this.loadTmOut)
      this.$store.commit('PROGRESS_FINISH')
      this.setTheme(this.activeSlice)
      this.startCountdown()
      // pre-mount the project as soon as the slide is actually showing imagery, so a plain scroll
      // (no click, no wheel threshold) already has real page content to carry the user into (H3)
      this.mountProject()
    },
    onSlideUnready (i) {
      this.$set(this.ready, i, false)
    },
    onSlideError (i) {
      this.$set(this.failed, i, true)
      if (i === this.active) this.skipIfPossible()
    },
    setTheme (slice) {
      if (this.inProject) return this.$store.commit('SET_THEME', 'light')
      const txt = slice && slice.primary.text_color
      this.$store.commit('SET_THEME', txt === 'white' ? 'dark' : null)
    },

    // --- bottom bar (B2) and right-edge arrow (B3)
    onMouse: _throttle(function (e) {
      if (this.isTouch || this.inProject) return
      this.nearBottom = e.clientY > this.winH * 0.5
      this.nearRightEdge = e.clientX > this.winW - 96
    }, 60),
    onStageLeave () {
      this.nearBottom = false
      this.nearRightEdge = false
    },
    onWheel (e) {
      if (this.expanded || !this.activePrj) return
      if (e.deltaY > 12) this.expand()
    },
    onKey (e) {
      const tag = (e.target && e.target.tagName) || ''
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
      if (this.inProject) return
      if (e.key === 'ArrowRight') this.goTo(1)
      else if (e.key === 'ArrowLeft') this.goTo(-1)
      else if (['ArrowDown', 'PageDown', ' '].includes(e.key) && !this.expanded && this.activePrj) {
        e.preventDefault()
        this.expand()
      }
    },
    onBarClick () {
      if (this.swiped) return
      this.expand()
    },
    // mounts the linked project under the slide (idempotent) without moving the page or the URL —
    // called automatically once a slide is ready (H3), and also from expand() below
    mountProject () {
      if (!this.activePrj) return
      if (this.expanded && this.expandedUid === this.activePrj.uid) return
      this.expanded = true
      this.expandedUid = this.activePrj.uid
      this.$store.commit('SET_INLINE_PRJ', this.expandedUid)
    },
    // an explicit ask to jump into the project (bar click, a wheel tick, keyboard, swipe up):
    // ensures it's mounted, then animates the scroll. History/URL changes only once the user has
    // actually scrolled into the project (see onScroll) — not the moment it's silently pre-mounted.
    expand () {
      if (!this.activePrj) return
      this.mountProject()
      this.jumpRequested = true
      this.$nextTick(() => this.scrollToProject())
    },
    scrollToProject () {
      const el = this.$refs.project
      if (el) this.$scrollTo(el, 900, { offset: 0 })
    },
    onProjectLoaded (ok) {
      if (!ok) return this.collapse()
      // the project's own data can finish loading after mount and settle the section to its full
      // height; only re-correct the scroll if the user actually asked to jump in (jumpRequested) —
      // a silently pre-mounted project (mountProject, H3) must never move the page on its own
      if (this.jumpRequested) this.$nextTick(() => this.scrollToProject())
    },
    collapse () {
      if (!this.expanded) return
      this.expanded = false
      this.expandedUid = null
      this.inProject = false
      this.jumpRequested = false
      this.$store.commit('SET_INLINE_PRJ', null)
      this.$store.commit('SET_NAV', null)
      if (this.pushedHistory) {
        try { window.history.replaceState(null, '', '/') } catch (e) { /* ignore */ }
      }
      this.pushedHistory = false
      window.scroll(0, 0)
      this.setTheme(this.activeSlice)
      this.startCountdown()
    },
    onPopState () {
      // browser back while the project is open under the slideshow: close it and stay on Selected Works
      if (this.expanded && window.location.pathname === '/') {
        this.pushedHistory = false
        this.collapse()
      }
    },
    onScroll (y) {
      const inPrj = this.expanded && y > this.winH * 0.4
      if (inPrj === this.inProject) return
      this.inProject = inPrj
      this.$store.commit('SET_INLINE_PRJ_ACTIVE', inPrj)
      this.setTheme(this.activeSlice)
      if (inPrj) {
        this.pause() // autoplay waits while the user reads the project
        this.nearBottom = false
        // deep link and back button: the address becomes the project's only once the user has
        // actually scrolled into it, not the moment it's silently pre-mounted by mountProject()
        if (this.expandedUid && !this.pushedHistory) {
          try {
            window.history.pushState({ gvhInline: this.expandedUid }, '', `/projects/${this.expandedUid}`)
            this.pushedHistory = true
          } catch (e) { /* history unavailable, carry on */ }
        }
      } else {
        this.startCountdown() // back at the top: the slideshow resumes
      }
    },

    // --- drag / swipe between works (B3), pull up into the project on touch (B2 mobile) —
    // a fast upward flick still pulls the project up; a plain scroll works too since it pre-mounts (H3)
    bindSwipe () {
      if (!this.$refs.stage) return
      this.bindDragTrack(this.$refs.stage, {
        onSwipeLeft: () => this.goTo(1),
        onSwipeRight: () => this.goTo(-1),
        onSwipeUp: () => { if (!this.expanded) this.expand() }
      })
    },

    // --- sound (B4)
    toggleSound () {
      this.muted = !this.muted
    },
    onVisibility () {
      document.hidden ? this.pause() : this.startCountdown()
    }
  },
  watch: {
    active () {
      if (this.expanded) this.collapse()
      this.muted = true
      this.pause()
      this.setTheme(this.activeSlice)
      this.armLoadTimeout()
      this.startCountdown()
    },
    barVisible (visible) {
      // while the pointer rests on the bar the deck holds still, so the caption the user reads is the one that opens
      if (this.isTouch) return
      visible ? this.pause() : this.startCountdown()
    }
  },
  mounted () {
    this.isTouch = isTouchDevice()
    this.$nextTick(() => this.bindSwipe())
    this.$store.state.bus.$on('newScrollY', this.onScroll)
    window.addEventListener('keydown', this.onKey)
    window.addEventListener('popstate', this.onPopState)
    document.addEventListener('visibilitychange', this.onVisibility)
    this.setTheme(this.activeSlice)
    this.armLoadTimeout()
  },
  beforeDestroy () {
    this.pause()
    clearTimeout(this.loadTmOut)
    this.$store.state.bus.$off('newScrollY', this.onScroll)
    window.removeEventListener('keydown', this.onKey)
    window.removeEventListener('popstate', this.onPopState)
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.$store.commit('SET_INLINE_PRJ', null)
  },
  beforeRouteEnter (to, from, next) {
    store.commit('PROGRESS_START')
    store.dispatch('getSingle', 'home').then(() => next())
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('SET_THEME')
    this.$store.commit('SET_INLINE_PRJ', null)
    next()
  },
  components: {
    'slide': ImageSlide,
    'vimeo_slide': VimeoSlide,
    ProjectPage
  }
}
</script>

<style>
.home__stage {
  cursor: grab;
  -webkit-user-select: none;
  user-select: none;
}
.home__stage--dragging { cursor: grabbing; }

/* each slide clips its own player: a landscape video covering a portrait phone is ~4x the screen
   width, and unclipped it spilled across the neighbouring slides (Iceland showing over Wellington Dam) */
.home__slide { overflow: hidden; }

.home__caption { transition: opacity 150ms; }
.home__caption--hidden { opacity: 0; }
.home__arrow { line-height: 0; opacity: .6; transition: opacity 150ms; }
.home__arrow:hover { opacity: 1; }
.home__chevron { display: inline-block; width: 1.4em; height: 1.4em; vertical-align: middle; }

.home__edge-arrow {
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease;
  cursor: pointer;
}
.home__edge-arrow--visible { opacity: 1; pointer-events: auto; }
.home__edge-chevron { display: inline-block; width: 2em; height: 2em; }

/* same movement as the top bar: 150ms slide, from the bottom edge */
.home__bar {
  transform: translateY(100%);
  opacity: 0;
  transition: transform 150ms ease, opacity 150ms ease;
  cursor: pointer;
}
.home__bar--visible {
  transform: none;
  opacity: 1;
}
.home__bar:focus { outline: none; }

/* neither the caption nor the summary may run under the centred arrow */
@media (min-width: 768px) {
  .home__bar-title { max-width: 38%; }
  .home__bar-summary { max-width: 40%; }
}

.home__scroll-arrow {
  width: 2.2em;
  height: 2.2em;
  color: black;
  opacity: 0.55;
  animation: home-scroll-nudge 1.9s ease-in-out infinite;
}
@keyframes home-scroll-nudge {
  0%, 100% { transform: translateY(-2px); opacity: 0.45; }
  50% { transform: translateY(3px); opacity: 0.95; }
}
@media (prefers-reduced-motion: reduce) {
  .home__scroll-arrow { animation: none; opacity: 0.8; }
}
.home__bar-summary {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
